import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

import { getVisualWorkflowPlanV1 } from "../../lib/brandStudioApi";

const WORKFLOW_LABELS = {
  photography_direction: "Фотография",
  material_direction: "Материалы / фактуры",
  graphic_presentation_direction: "Графический язык / подача",
};

const STATUS_LABELS = {
  ready: "готов",
  ready_without_direct_references: "готов без прямых референсов",
  not_applicable: "не требуется",
  blocked: "заблокирован",
};

const SIGNAL_LABELS = {
  color: "Цвет",
  typography: "Типографика",
  composition: "Композиция",
  photography: "Фотография",
  graphics: "Графика",
  packaging: "Упаковка",
  material_texture: "Материал / фактура",
  mood: "Настроение",
  logo_mark: "Логотип / знак",
  brandbook_presentation: "Подача брендбука",
  whole: "Всё целиком",
  other: "Другое",
};

const LOGO_LABELS = {
  existing: "Есть существующий логотип",
  need_new: "Нужен новый логотип",
  not_needed: "Логотип пока не нужен",
  unset: "Решение не задано",
};

function CandidateChip({ candidate }) {
  return (
    <div className="rounded-[12px] border border-[#E4D8C8] bg-[#FFFDF9] px-3 py-2">
      <div className="text-[10.5px] font-medium text-[#4B392B]">
        #{candidate.order + 1} · ранг {candidate.rank}
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {(candidate.signals || []).map((signal) => (
          <span
            key={signal}
            className="rounded-full border border-[#E3D6C4] px-2 py-0.5 text-[9px] text-[#7A624D]"
          >
            {SIGNAL_LABELS[signal] || signal}
          </span>
        ))}
      </div>
      {candidate.identity_content_present && (
        <div className="mt-1 text-[9px] text-[#8B5D52]">
          Есть визуальный контент айдентики — использовать только как контекст.
        </div>
      )}
    </div>
  );
}

export default function VisualWorkflowPlannerOwnerPanelV1() {
  const [status, setStatus] = useState("loading");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getVisualWorkflowPlanV1()
      .then((value) => {
        if (cancelled) return;
        setResult(value);
        setStatus("ready");
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(
          requestError?.response?.data?.detail ||
            "Не удалось открыть Workflow Planner.",
        );
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="visual-workflow-planner-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        ПРОВЕРКА ВЛАДЕЛЬЦА · WP1
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        План визуальных рабочих сценариев
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Brand Studio берёт уже сохранённый Structured Visual Style и разводит
        референсы по конкретным задачам. Никаких новых AI-вызовов, prompt или
        provider selection на этом этапе нет.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" />
          Собираю deterministic plan…
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {String(error)}
        </div>
      )}

      {status === "ready" && result?.result === "not_ready" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {result.reason || "Сначала нужен Structured Visual Style."}
        </div>
      )}

      {status === "ready" && result?.plan && (
        <div className="mt-6 space-y-4 border-t border-[#E2D5C3] pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} />
              Visual Style v{result.plan.visual_style_version}
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              3 профессиональных сценария · вызовы провайдера: {result.plan.provider_call_count}
              {" · "}ИИ-синтез: {result.plan.ai_synthesis_call_count}
              {" · "}стоимость: {result.plan.estimated_cost}
            </span>
          </div>

          {(result.plan.global_rules?.explicit_decisions || []).length > 0 && (
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Глобальные решения пользователя · обязательны во всех сценариях
              </div>
              <div className="mt-2 space-y-1.5">
                {result.plan.global_rules.explicit_decisions.map((item) => (
                  <div key={item.id} className="text-[11.5px] leading-5 text-[#4C392B]">
                    • {item.text}
                  </div>
                ))}
              </div>
            </article>
          )}

          <div className="grid gap-3 lg:grid-cols-3">
            {result.plan.workflows.map((workflow) => (
              <article
                key={workflow.workflow_type}
                className="rounded-[16px] border border-[#DED0BC] bg-white p-4"
                data-testid={"visual-workflow-card-" + workflow.workflow_type}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                      {WORKFLOW_LABELS[workflow.workflow_type] || workflow.workflow_type}
                    </div>
                    <p className="mt-2 text-[11px] leading-5 text-[#655141]">
                      {workflow.purpose}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#F3EBDD] px-2.5 py-1 text-[9px] text-[#6D5842]">
                    {STATUS_LABELS[workflow.readiness] || workflow.readiness}
                  </span>
                </div>

                <div className="mt-3 text-[10px] leading-5 text-[#8A745E]">
                  Релевантных решений: {workflow.style_decision_ids?.length || 0}
                  <br />
                  Прямых кандидатов-референсов: {workflow.reference_candidates?.length || 0}
                </div>

                <div className="mt-3 space-y-2">
                  {(workflow.reference_candidates || []).slice(0, 5).map((candidate) => (
                    <CandidateChip key={candidate.reference_id} candidate={candidate} />
                  ))}
                  {(workflow.reference_candidates || []).length > 5 && (
                    <div className="text-[9.5px] text-[#927A5B]">
                      + ещё {workflow.reference_candidates.length - 5} кандидатов в деталях
                    </div>
                  )}
                </div>

                <div className="mt-3 text-[9.5px] leading-4 text-[#8A745E]">
                  {workflow.readiness_reason}
                </div>
              </article>
            ))}
          </div>

          <article className="rounded-[14px] border border-[#DED0BC] bg-[#FFFCF6] p-4">
            <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
              Контекст точной айдентики
            </div>
            <div className="mt-2 text-[11px] leading-5 text-[#655141]">
              Логотип: {LOGO_LABELS[result.plan.logo_state] || result.plan.logo_state}
              <br />
              Точная айдентика остаётся задачей Controlled Renderer / следующих систем.
            </div>
          </article>

          <details className="rounded-[14px] border border-[#DED0BC] bg-white">
            <summary className="cursor-pointer px-4 py-3 text-[11.5px] font-medium text-[#4C392B]">
              Полный routing референсов и технические детали
            </summary>
            <div className="border-t border-[#EEE4D6] px-4 py-4">
              <div className="text-[10px] leading-5 text-[#796652]">
                Версия реестра: {result.plan.workflow_registry_version}
                <br />
                Отпечаток плана: {result.plan.plan_fingerprint}
                <br />
                Провайдер: не выбран · модель: не выбрана · промпт: не создаётся
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-3">
                {result.plan.workflows.map((workflow) => (
                  <div key={workflow.workflow_type}>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#927A5B]">
                      {WORKFLOW_LABELS[workflow.workflow_type] || workflow.workflow_type}
                    </div>
                    <div className="mt-2 space-y-2">
                      {(workflow.reference_candidates || []).map((candidate) => (
                        <CandidateChip key={candidate.reference_id} candidate={candidate} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </details>
        </div>
      )}
    </section>
  );
}
